/**
 * CustomerActivityCard - Visar kundens aktivitet på estimatet
 * 
 * Visar när kunden öppnade estimatet, hur många gånger,
 * senaste aktivitet och detaljerad aktivitetslogg
 */

import React from 'react';

const CustomerActivityCard = ({ activity, customerName, onShowAllActivities }) => {
  if (!activity || !activity.hasBeenViewed) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
          <h4 className="text-sm font-medium text-gray-600">Kundaktivitet</h4>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {customerName} har inte öppnat estimatet än
        </p>
        <div className="text-xs text-gray-400 mt-1">
          Debug: activity={activity ? 'finns' : 'null'}, hasBeenViewed={activity?.hasBeenViewed}
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Okänt';
    return new Date(dateString).toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityColor = () => {
    const now = new Date();
    const lastViewed = new Date(activity.lastViewAt);
    const hoursSinceLastView = (now - lastViewed) / (1000 * 60 * 60);
    
    if (hoursSinceLastView < 24) return 'bg-green-500'; // Grön - sett inom 24h
    if (hoursSinceLastView < 72) return 'bg-yellow-500'; // Gul - sett inom 72h
    return 'bg-gray-400'; // Grå - längre sedan
  };

  const getActivityText = () => {
    const now = new Date();
    const lastViewed = new Date(activity.lastViewAt);
    const hoursSinceLastView = (now - lastViewed) / (1000 * 60 * 60);
    
    if (hoursSinceLastView < 1) return '🔥 Mycket aktiv (<1h)';
    if (hoursSinceLastView < 24) return '🟢 Aktiv (<24h)';
    if (hoursSinceLastView < 72) return '🟡 Måttligt aktiv (<72h)';
    return '⚪ Låg aktivitet (>72h)';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${getActivityColor()}`}></div>
          <h4 className="text-sm font-medium text-gray-900">Kundaktivitet</h4>
        </div>
        <span className="text-xs text-gray-500">
          {activity.totalViews} visning{activity.totalViews !== 1 ? 'ar' : ''}
        </span>
      </div>
      
      <div className="space-y-2">
        <p className="text-sm text-gray-700">
          {getActivityText()}
        </p>
        
        <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
          <div>
            <span className="font-medium">Första visning:</span>
            <br />
            {formatDate(activity.firstViewed)}
          </div>
          <div>
            <span className="font-medium">Senaste visning:</span>
            <br />
            {formatDate(activity.lastViewAt)}
          </div>
        </div>
        
        {activity.recentActivities && activity.recentActivities.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
              Visa senaste aktivitet ({activity.recentActivities.length})
            </summary>
            <div className="mt-2 space-y-1">
              {activity.recentActivities.map((activityItem, index) => {
                const getActivityIcon = (type) => {
                  switch (type) {
                    case 'page_view': return '👁️';
                    case 'section_expanded': return '📂';
                    case 'comment_draft': return '✍️';
                    case 'comment_sent': return '💬';
                    case 'price_focus': return '💰';
                    default: return '📊';
                  }
                };

                const getActivityDescription = (item) => {
                  switch (item.activityType) {
                    case 'page_view':
                      return 'Öppnade estimatet';
                    case 'section_expanded':
                      return `${item.section ? `Expanderade "${item.section}"` : 'Expanderade sektion'}`;
                    case 'comment_draft':
                      if (item.draftText) {
                        // Loggen sparar hela texten som backup; kortet visar bara början
                        const utdrag = item.draftText.length > 100 ? `${item.draftText.substring(0, 100)}…` : item.draftText;
                        return `Skrev utkast: "${utdrag}"${item.section ? ` i "${item.section}"` : ''}`;
                      } else {
                        return `Skrev utkast (${item.draftLength} tecken)${item.section ? ` i "${item.section}"` : ''}`;
                      }
                    case 'comment_sent':
                      return 'Skickade kommentar';
                    case 'price_focus':
                      return `Tittade på pris för ${item.itemName}`;
                    default:
                      return item.activityType;
                  }
                };

                return (
                  <div key={index} className="text-xs text-gray-500 pl-2 border-l-2 border-gray-200 flex items-start space-x-1">
                    <span className="flex-shrink-0">{getActivityIcon(activityItem.activityType)}</span>
                    <div className="flex-1">
                      <div>{formatDate(activityItem.timestamp)} - {activityItem.viewerName}</div>
                      <div className="text-gray-400 italic">{getActivityDescription(activityItem)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}
        
        {/* Visa alla-knapp om det finns fler än 10 aktiviteter */}
        {activity.totalActivities > 10 && onShowAllActivities && (
          <div className="mt-3 text-center">
            <button
              onClick={onShowAllActivities}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Visa alla {activity.totalActivities} aktiviteter
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerActivityCard;
